// @ts-nocheck
/**
 * Approval Workflow Dashboard Component
 * Carbon Design System dashboard for content approval workflows
 * Displays pending approvals, review queue, and workflow management
 */

import React, { useState, useEffect } from 'react';
import {
  DataTable,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableHeader,
  TableBody,
  TableCell,
  TableToolbar,
  TableToolbarContent,
  TableToolbarSearch,
  Button,
  Tag,
  Tile,
  Grid,
  Column,
  Modal,
  TextArea,
  RadioButtonGroup,
  RadioButton,
  InlineNotification,
  ProgressIndicator,
  ProgressStep,
  OverflowMenu,
  OverflowMenuItem,
  Tabs,
  TabList,
  Tab,
  TabPanels,
  TabPanel,
  Loading,
  SkeletonText,
  Breadcrumb,
  BreadcrumbItem,
} from '@carbon/react';
import {
  Checkmark,
  Close,
  View,
  User,
  Time,
  Warning,
  Information,
  Send,
  TaskComplete,
  InProgress,
  Pending,
} from '@carbon/icons-react';

import type {
  ApprovalWorkflowDashboardState,
  WorkflowItem,
  ApproverInfo,
  WorkflowComment,
  WorkflowNotification,
  WorkflowStatus,
  WorkflowPriority,
  ApprovalStatus,
  ContentPreviewOptions,
} from '../../../shared/interfaces/ContentManagementAdmin';

interface ApprovalWorkflowDashboardProps {
  onWorkflowAction: (workflowId: string, action: string, comment?: string) => Promise<void>;
  onContentPreview: (options: ContentPreviewOptions) => void;
  refreshTrigger?: number;
}

const ApprovalWorkflowDashboard: React.FC<ApprovalWorkflowDashboardProps> = ({
  onWorkflowAction,
  onContentPreview,
  refreshTrigger = 0,
}) => {
  const [state, setState] = useState<ApprovalWorkflowDashboardState>({
    pendingApprovals: [],
    myReviews: [],
    completedWorkflows: [],
    notifications: [],
    loading: true,
    error: null,
  });

  const [reviewModalOpen, setReviewModalOpen] = useState(false);
  const [selectedWorkflow, setSelectedWorkflow] = useState<WorkflowItem | null>(null);
  const [reviewDecision, setReviewDecision] = useState<'approved' | 'rejected'>('approved');
  const [reviewComment, setReviewComment] = useState('');
  const [activeTab, setActiveTab] = useState(0);

  // Load workflow data
  useEffect(() => {
    loadWorkflowData();
  }, [refreshTrigger]);

  const loadWorkflowData = async () => {
    setState(prev => ({ ...prev, loading: true, error: null }));

    try {
      const [pendingResponse, myReviewsResponse, completedResponse, notificationsResponse] = await Promise.all([
        fetch('/api/v1/content-management/workflow/pending'),
        fetch('/api/v1/content-management/workflow/my-reviews'),
        fetch('/api/v1/content-management/workflow/completed?limit=10'),
        fetch('/api/v1/content-management/workflow/notifications'),
      ]);

      const [pending, myReviews, completed, notifications] = await Promise.all([
        pendingResponse.ok ? pendingResponse.json() : { data: [] },
        myReviewsResponse.ok ? myReviewsResponse.json() : { data: [] },
        completedResponse.ok ? completedResponse.json() : { data: [] },
        notificationsResponse.ok ? notificationsResponse.json() : { data: [] },
      ]);

      setState(prev => ({
        ...prev,
        pendingApprovals: pending.data || [],
        myReviews: myReviews.data || [],
        completedWorkflows: completed.data || [],
        notifications: notifications.data || [],
        loading: false,
      }));
    } catch (error) {
      setState(prev => ({
        ...prev,
        error: error instanceof Error ? error.message : 'Failed to load workflow data',
        loading: false,
      }));
    }
  };

  const handleReviewSubmit = async () => {
    if (!selectedWorkflow) return;

    try {
      await onWorkflowAction(selectedWorkflow.id, reviewDecision, reviewComment);
      setReviewModalOpen(false);
      setSelectedWorkflow(null);
      setReviewComment('');
      loadWorkflowData(); // Refresh data
    } catch (error) {
      console.error('Review submission failed:', error);
    }
  };

  const handlePreviewContent = (workflow: WorkflowItem) => {
    onContentPreview({
      contentId: workflow.contentId,
      previewMode: 'draft',
      deviceType: 'desktop',
    });
  };

  const getPriorityTag = (priority: WorkflowPriority) => {
    const priorityConfig = {
      urgent: { type: 'red' as const, label: 'Urgent' },
      high: { type: 'magenta' as const, label: 'High' },
      medium: { type: 'blue' as const, label: 'Medium' },
      low: { type: 'gray' as const, label: 'Low' },
    };

    const config = priorityConfig[priority] || priorityConfig.medium;
    return <Tag type={config.type}>{config.label}</Tag>;
  };

  const getStatusTag = (status: WorkflowStatus) => {
    const statusConfig = {
      pending: { type: 'blue' as const, label: 'Pending' },
      approved: { type: 'green' as const, label: 'Approved' },
      rejected: { type: 'red' as const, label: 'Rejected' },
      expired: { type: 'gray' as const, label: 'Expired' },
    };

    const config = statusConfig[status] || statusConfig.pending;
    return <Tag type={config.type}>{config.label}</Tag>;
  };

  const getApprovalStatusIcon = (status: ApprovalStatus) => {
    switch (status) {
      case 'approved':
        return <Checkmark size={16} className="approval-approved" />;
      case 'rejected':
        return <Close size={16} className="approval-rejected" />;
      default:
        return <Pending size={16} className="approval-pending" />;
    }
  };

  const formatTimeAgo = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInHours = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60));

    if (diffInHours < 1) return 'Just now';
    if (diffInHours < 24) return `${diffInHours}h ago`;

    const diffInDays = Math.floor(diffInHours / 24);
    if (diffInDays < 7) return `${diffInDays}d ago`;

    return date.toLocaleDateString();
  };

  const renderWorkflowTable = (workflows: WorkflowItem[], title: string) => {
    const headers = [
      { key: 'contentTitle', header: 'Content' },
      { key: 'contentType', header: 'Type' },
      { key: 'submittedBy', header: 'Submitted By' },
      { key: 'priority', header: 'Priority' },
      { key: 'approvals', header: 'Approvals' },
      { key: 'submittedAt', header: 'Submitted' },
      { key: 'actions', header: 'Actions' },
    ];

    const rows = workflows.map(workflow => ({
      id: workflow.id,
      contentTitle: workflow.contentTitle,
      contentType: <Tag type="cool-gray">{workflow.contentType}</Tag>,
      submittedBy: (
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <User size={16} />
          {workflow.submittedBy}
        </div>
      ),
      priority: getPriorityTag(workflow.priority),
      approvals: (
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <span>
            {workflow.currentApprovals}/{workflow.requiredApprovals}
          </span>
          <ProgressIndicator currentIndex={workflow.currentApprovals - 1} spaceEqually>
            {Array.from({ length: workflow.requiredApprovals }, (_, i) => (
              <ProgressStep
                key={i}
                complete={i < workflow.currentApprovals}
                current={i === workflow.currentApprovals}
                label={`Approval ${i + 1}`}
              />
            ))}
          </ProgressIndicator>
        </div>
      ),
      submittedAt: formatTimeAgo(workflow.submittedAt),
      actions: (
        <OverflowMenu flipped>
          <OverflowMenuItem itemText="Preview Content" onClick={() => handlePreviewContent(workflow)} />
          <OverflowMenuItem
            itemText="Review"
            onClick={() => {
              setSelectedWorkflow(workflow);
              setReviewModalOpen(true);
            }}
          />
          <OverflowMenuItem itemText="View Details" />
        </OverflowMenu>
      ),
    }));

    return (
      <div style={{ marginBottom: '2rem' }}>
        <h3 style={{ marginBottom: '1rem' }}>{title}</h3>
        <DataTable
          rows={rows}
          headers={headers}
          render={({ rows, headers, getHeaderProps, getRowProps, getTableProps }) => (
            <TableContainer>
              <Table {...getTableProps()}>
                <TableHead>
                  <TableRow>
                    {headers.map(header => (
                      <TableHeader key={header.key} {...getHeaderProps({ header })}>
                        {header.header}
                      </TableHeader>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={headers.length} style={{ textAlign: 'center', padding: '2rem' }}>
                        <p style={{ color: 'var(--cds-text-02)' }}>No workflows found</p>
                      </TableCell>
                    </TableRow>
                  ) : (
                    rows.map(row => (
                      <TableRow key={row.id} {...getRowProps({ row })}>
                        {row.cells.map(cell => (
                          <TableCell key={cell.id}>{cell.value}</TableCell>
                        ))}
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        />
      </div>
    );
  };

  const renderWorkflowDetails = (workflow: WorkflowItem) => (
    <Grid>
      <Column lg={8} md={6} sm={4}>
        <Tile style={{ marginBottom: '1rem' }}>
          <h4 style={{ marginBottom: '1rem' }}>Content Details</h4>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
            <div>
              <strong>Title:</strong> {workflow.contentTitle}
            </div>
            <div>
              <strong>Type:</strong> {workflow.contentType}
            </div>
            <div>
              <strong>Submitted by:</strong> {workflow.submittedBy}
            </div>
            <div>
              <strong>Submitted:</strong> {new Date(workflow.submittedAt).toLocaleString()}
            </div>
            <div>
              <strong>Priority:</strong> {getPriorityTag(workflow.priority)}
            </div>
          </div>
        </Tile>

        <Tile>
          <h4 style={{ marginBottom: '1rem' }}>Approval Progress</h4>
          <div style={{ marginBottom: '1rem' }}>
            <ProgressIndicator currentIndex={workflow.currentApprovals - 1} spaceEqually>
              {workflow.approvers.map((approver, index) => (
                <ProgressStep
                  key={approver.userId}
                  complete={approver.status === 'approved'}
                  current={approver.status === 'pending'}
                  invalid={approver.status === 'rejected'}
                  label={approver.name}
                  description={approver.role}
                />
              ))}
            </ProgressIndicator>
          </div>

          <div>
            <h5 style={{ marginBottom: '0.5rem' }}>Approver Details</h5>
            {workflow.approvers.map(approver => (
              <div
                key={approver.userId}
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: '0.5rem',
                  marginBottom: '0.5rem',
                  backgroundColor: 'var(--cds-layer-01)',
                  borderRadius: '4px',
                }}
              >
                <div>
                  <div style={{ fontWeight: 500 }}>{approver.name}</div>
                  <div style={{ color: 'var(--cds-text-02)', fontSize: '0.875rem' }}>{approver.role}</div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  {getApprovalStatusIcon(approver.status)}
                  {approver.reviewedAt && (
                    <span style={{ fontSize: '0.875rem', color: 'var(--cds-text-02)' }}>
                      {formatTimeAgo(approver.reviewedAt)}
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </Tile>
      </Column>

      <Column lg={8} md={6} sm={4}>
        <Tile>
          <h4 style={{ marginBottom: '1rem' }}>Comments & History</h4>
          <div className="comments-history">
            {workflow.comments.map(comment => (
              <div
                key={comment.id}
                style={{ marginBottom: '1rem', padding: '1rem', border: '1px solid #e0e0e0', borderRadius: '4px' }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                  <strong>{comment.userName}</strong>
                  <span style={{ color: 'var(--cds-text-02)', fontSize: '0.875rem' }}>
                    {formatTimeAgo(comment.timestamp)}
                  </span>
                  <Tag
                    type={
                      comment.type === 'approval'
                        ? 'green'
                        : comment.type === 'rejection'
                          ? 'red'
                          : comment.type === 'system'
                            ? 'gray'
                            : 'blue'
                    }
                    size="sm"
                  >
                    {comment.type}
                  </Tag>
                </div>
                <p style={{ margin: 0 }}>{comment.content}</p>
              </div>
            ))}
          </div>
        </Tile>
      </Column>
    </Grid>
  );

  if (state.loading) {
    return (
      <div>
        <SkeletonText heading />
        <SkeletonText paragraph lineCount={3} />
        <Loading />
      </div>
    );
  }

  if (state.error) {
    return (
      <InlineNotification
        kind="error"
        title="Workflow Dashboard Error"
        subtitle={state.error}
        onCloseButtonClick={() => setState(prev => ({ ...prev, error: null }))}
      />
    );
  }

  return (
    <div className="approval-workflow-dashboard">
      {/* Dashboard Header */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '2rem',
        }}
      >
        <div>
          <h2>Approval Workflow Dashboard</h2>
          <p style={{ color: 'var(--cds-text-02)' }}>Manage content approval workflows and reviews</p>
        </div>

        <div style={{ display: 'flex', gap: '1rem' }}>
          <Tile style={{ padding: '1rem', textAlign: 'center' }}>
            <div style={{ fontSize: '1.5rem', fontWeight: 600 }}>{state.pendingApprovals.length}</div>
            <div style={{ color: 'var(--cds-text-02)', fontSize: '0.875rem' }}>Pending Approvals</div>
          </Tile>
          <Tile style={{ padding: '1rem', textAlign: 'center' }}>
            <div style={{ fontSize: '1.5rem', fontWeight: 600 }}>{state.myReviews.length}</div>
            <div style={{ color: 'var(--cds-text-02)', fontSize: '0.875rem' }}>My Reviews</div>
          </Tile>
        </div>
      </div>

      {/* Workflow Tabs */}
      <Tabs selectedIndex={activeTab} onChange={setActiveTab}>
        <TabList>
          <Tab>Pending Approvals ({state.pendingApprovals.length})</Tab>
          <Tab>My Reviews ({state.myReviews.length})</Tab>
          <Tab>Completed Workflows</Tab>
          <Tab>Notifications</Tab>
        </TabList>

        <TabPanels>
          <TabPanel>{renderWorkflowTable(state.pendingApprovals, 'Pending Approvals')}</TabPanel>

          <TabPanel>{renderWorkflowTable(state.myReviews, 'My Reviews')}</TabPanel>

          <TabPanel>{renderWorkflowTable(state.completedWorkflows, 'Completed Workflows')}</TabPanel>

          <TabPanel>
            <div>
              <h3 style={{ marginBottom: '1rem' }}>Recent Notifications</h3>
              {state.notifications.length === 0 ? (
                <Tile style={{ textAlign: 'center', padding: '2rem' }}>
                  <p style={{ color: 'var(--cds-text-02)' }}>No recent notifications</p>
                </Tile>
              ) : (
                state.notifications.map(notification => (
                  <InlineNotification
                    key={notification.id}
                    kind={
                      notification.type === 'content_approved'
                        ? 'success'
                        : notification.type === 'content_rejected'
                          ? 'error'
                          : 'info'
                    }
                    title={notification.title}
                    subtitle={notification.message}
                    style={{ marginBottom: '1rem' }}
                  />
                ))
              )}
            </div>
          </TabPanel>
        </TabPanels>
      </Tabs>

      {/* Review Modal */}
      <Modal
        open={reviewModalOpen}
        onRequestClose={() => setReviewModalOpen(false)}
        modalHeading="Review Content"
        modalLabel="Approval Workflow"
        primaryButtonText="Submit Review"
        secondaryButtonText="Cancel"
        onRequestSubmit={handleReviewSubmit}
        size="lg"
      >
        {selectedWorkflow && (
          <div>
            {renderWorkflowDetails(selectedWorkflow)}

            <div style={{ marginTop: '2rem', paddingTop: '1rem', borderTop: '1px solid var(--cds-border-subtle)' }}>
              <h4 style={{ marginBottom: '1rem' }}>Your Review</h4>

              <RadioButtonGroup
                name="review-decision"
                valueSelected={reviewDecision}
                onChange={setReviewDecision}
                style={{ marginBottom: '1rem' }}
              >
                <RadioButton labelText="Approve" value="approved" id="approve" />
                <RadioButton labelText="Reject" value="rejected" id="reject" />
              </RadioButtonGroup>

              <TextArea
                id="review-comment"
                labelText="Comments"
                placeholder="Add your review comments..."
                rows={4}
                value={reviewComment}
                onChange={e => setReviewComment(e.target.value)}
              />
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default ApprovalWorkflowDashboard;
